import { ICategory } from '@/interfaces/ICategory';
import { IItem } from '@/interfaces/IItem';
import { IOption } from '@/interfaces/IOption';
import { IOptionItem } from '@/interfaces/IOptionItem';

/**
 * «Menu health» filters of the Menú page (plan §5.6). One implementation,
 * shared with the GPTHelp catalog tools (§5.20) so the agent and the page
 * always agree on which products lack a photo, a price….
 */
export type TCatalogFilter =
  | 'all'
  | 'hidden'
  | 'no-photo'
  | 'no-description'
  | 'no-price'
  | 'discounted'
  | 'scheduled';

/** Chip order. */
export const CATALOG_FILTERS: readonly TCatalogFilter[] = [
  'all',
  'hidden',
  'no-photo',
  'no-description',
  'no-price',
  'discounted',
  'scheduled',
];

export function isCatalogFilter(value: unknown): value is TCatalogFilter {
  return (
    typeof value === 'string' &&
    (CATALOG_FILTERS as readonly string[]).includes(value)
  );
}

/** Stored branch lists hold strings (`simple-array`). */
export function hiddenInBranch(
  entity: Pick<IItem | ICategory, 'hiddenBranchesIds'>,
  branchId: number | null
): boolean {
  if (branchId === null || !entity.hiddenBranchesIds) return false;
  const id = String(branchId);
  return entity.hiddenBranchesIds.some((hidden) => hidden.trim() === id);
}

/** What the size helpers read: v2 items carry sizes in `options`. */
export type TSizesSource = Pick<IItem, 'options'>;

/**
 * The product's sizes group: its live main option with the lowest id, the
 * rule the API applies (`items-catalog.service.ts` `findMainOptions`).
 */
export function sizeOption(item: TSizesSource): IOption | null {
  let found: IOption | null = null;
  for (const option of item.options ?? [])
    if (
      option.main &&
      !option.deletedAt &&
      (found === null || (option.id ?? 0) < (found.id ?? 0))
    )
      found = option;
  return found;
}

/** Live sizes sorted by `(index, id)`; [] for a single price. */
export function sortedSizes(item: TSizesSource): IOptionItem[] {
  const sizes = (sizeOption(item)?.optionItems ?? []).filter(
    (size) => !size.deletedAt
  );
  return sizes.sort(
    (a, b) => (a.index ?? 0) - (b.index ?? 0) || (a.id ?? 0) - (b.id ?? 0)
  );
}

export interface ISavedSize {
  id: number;
  optionId: number;
  title: string;
  description: string | null;
}

/** The live sizes with their ids (translations and stock hang from them). */
export function savedSizes(item: TSizesSource): ISavedSize[] {
  return sortedSizes(item).flatMap((size) =>
    size.id !== undefined
      ? [
          {
            id: size.id,
            optionId: size.optionId,
            title: size.title,
            description: size.description ?? null,
          },
        ]
      : []
  );
}

/** Priced by size (the server's `hasSizes`: a main option with live sizes). */
export function hasSizes(item: TSizesSource): boolean {
  return sortedSizes(item).length > 0;
}

export function activeSizes(item: TSizesSource): IOptionItem[] {
  return sortedSizes(item).filter((size) => size.active);
}

/** The prices a customer can pay: the active sizes' costs, or the single price. */
export function activePrices(item: Pick<IItem, 'options' | 'price'>): number[] {
  const sizes = activeSizes(item);
  return sizes.length ? sizes.map((size) => size.cost) : [item.price];
}

export function hasDiscount(
  item: Pick<IItem, 'options' | 'discountedPrice'>
): boolean {
  return (
    item.discountedPrice != null ||
    activeSizes(item).some((size) => size.discountedCost != null)
  );
}

export interface ICatalogFilterContext {
  branchId: number | null;
}

export function matchesCatalogFilter(
  filter: TCatalogFilter,
  item: IItem,
  category: ICategory | null,
  ctx: ICatalogFilterContext
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'hidden':
      return !item.visible || hiddenInBranch(item, ctx.branchId);
    case 'no-photo':
      return !item.picture;
    case 'no-description':
      return !item.description?.trim();
    case 'no-price':
      // POS-only products are priced at the counter.
      return (
        !category?.posOnly &&
        activePrices(item).every((price) => Number(price) === 0)
      );
    case 'discounted':
      return hasDiscount(item);
    case 'scheduled':
      return !!item.hourRanges?.length || !!category?.hourRanges?.length;
  }
}

export interface ICatalogFilterRow {
  item: IItem;
  category: ICategory | null;
}

/** Chip counts over the current menu's products. */
export function countCatalogFilters(
  rows: readonly ICatalogFilterRow[],
  ctx: ICatalogFilterContext
): Record<TCatalogFilter, number> {
  const counts: Record<TCatalogFilter, number> = {
    all: 0,
    hidden: 0,
    'no-photo': 0,
    'no-description': 0,
    'no-price': 0,
    discounted: 0,
    scheduled: 0,
  };
  for (const { item, category } of rows)
    for (const filter of CATALOG_FILTERS)
      if (matchesCatalogFilter(filter, item, category, ctx)) counts[filter]++;
  return counts;
}
